import { WeenatColor } from '@weenat/theme'
import Text from 'app/src/kit/Text'
import CountIndicator from 'app/src/kit/CountIndicator'
import Icons from 'app/src/kit/Icons'
import isNil from 'lodash-es/isNil'
import isString from 'lodash-es/isString'
import { css, styled } from 'styled-components'
import { useChipsTabsContext } from './ChipsTabsContext'

interface ChipTabProps {
  $isActive: boolean
  $isRounded?: boolean
  $isShadowed?: boolean
  $isDisabled?: boolean
}

const ChipTab = styled(Box)<ChipTabProps>`
  align-items: center;

  ${(p) =>
    p.$isShadowed &&
    css`
      box-shadow: ${(p) => p.theme.shadows.lg.boxShadow};
    `}

  padding: 4px 8px;
  border-radius: ${(p) => (p.$isRounded ? p.theme.radiuses.xl : p.theme.radiuses.md)}px;
  border: 1px solid
    ${(p) => (p.$isActive ? p.theme.colors.primary[800] : p.theme.colors.grayscale[300])};
  background-color: ${(p) =>
    p.$isActive ? p.theme.colors.primary[500] : p.theme.colors.grayscale.white};
  cursor: ${(p) => (p.$isDisabled ? 'inherit' : 'pointer')};
  height: 100%;

  &:hover {
    background-color: ${(p) =>
      p.$isDisabled ? p.theme.colors.grayscale.white : p.theme.colors.primary[500]};

    p {
      color: ${(p) =>
        p.$isDisabled ? p.theme.colors.grayscale[300] : p.theme.colors.grayscale.white};
    }

    svg {
      fill: ${(p) =>
        p.$isDisabled ? p.theme.colors.grayscale[300] : p.theme.colors.grayscale.white};
    }
  }

  transition: background-color 0.2s ease-in-out;
`

interface ChipsTabsItemProps {
  value: string | number
  label: string | React.ReactNode
  /** Background color when this item is active */
  activeColor?: WeenatColor
  /** Key of an icon from the Icons map */
  iconName?: keyof typeof Icons
  /** Count badge displayed to the right of the label */
  indicator?: string | number
  isDisabled?: boolean
  $isRounded?: boolean
  $isShadowed?: boolean
}

function ChipsTabsItem({
  value,
  label,
  activeColor: _activeColor,
  iconName,
  indicator,
  isDisabled,
  $isRounded,
  $isShadowed
}: ChipsTabsItemProps) {
  const { currentValue, onChange } = useChipsTabsContext()

  const $isActive = currentValue === value
  const textColor: WeenatColor = isDisabled
    ? 'grayscale.100'
    : $isActive
      ? 'grayscale.white'
      : 'grayscale.black'

  const BoldLabel = isString(label) ? (
    <Text $fontWeight='semiBold' $ellipsis $textAlign='center' $color={textColor}>
      {label.trim()}
    </Text>
  ) : (
    label
  )

  const Icn = !isNil(iconName) ? Icons[iconName] : null

  return (
    <Box $mr='md'>
      <ChipTab
        $isRounded={$isRounded}
        $isActive={$isActive}
        onClick={() => {
          if (!isDisabled) onChange(value)
        }}
        $isShadowed={$isShadowed}
        $isDisabled={isDisabled}
      >
        <Flex $alignItems='center' $gap='md'>
          {!isNil(Icn) ? <Icn $size='md' $color={textColor} /> : null}
          {BoldLabel}
          {!isNil(indicator) ? (
            <CountIndicator
              count={indicator}
              backgroundColor={textColor}
              color={isDisabled ? 'grayscale.100' : !$isActive ? 'grayscale.white' : 'primary.500'}
            />
          ) : null}
        </Flex>
      </ChipTab>
    </Box>
  )
}

export default ChipsTabsItem
